/** The Pixi canvas, mounted as a sibling of the panel layer (D8).
 *
 * This component owns pointer events inside its own bounds and calls
 * `stopPropagation` nowhere. It decides nothing about the game: every path it
 * draws came from the session's own Dijkstra pass, every command it issues goes
 * through the session, and the presenter is handed a `PlayerObservation` and a
 * `GameEvent[]` and nothing else.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ContentRegistry, HeroId, MapObjectDef, ObjectId, ObservedHero, ObservedObject,
  ObservedTown, PlayerObservation, TownId,
} from "@game/core";
import { UNREACHABLE_COST } from "@game/core";
import type { CursorKind, HoverPath, PathStep } from "@game/render";
import { AdventurePresenter, TILE_SIZE, pixelPoint } from "@game/render";
import type { CameraView, HoverTarget, MapExtent, Selection, TouchCell } from "@game/ui";
import {
  LONG_PRESS_MS, TOUCH_PAN_SLOP_PX, clampCamera, confirmOrArm, objectCovering,
  pinchZoomSteps, pointerDistance, readMapClick, usePhoneLayout,
} from "@game/ui";
import type { GameSession } from "../game/store.js";
import { app } from "../game/app.js";
import { ensureGpuAtlas } from "../game/gpuAtlas.js";
import type { Aiming } from "./aiming.js";
import { aimingReach, isAimTarget } from "./aiming.js";

export interface WorldCanvasProps {
  readonly session: GameSession;
  readonly presenterRef: { current: AdventurePresenter | null };
  readonly camera: CameraView;
  /**
   * Magnification asked for, in CSS pixels per world pixel; 1 when absent.
   * The presenter snaps it (`clampZoom`) and the viewport's cell count is
   * read back from what it actually uses, so the camera's `widthCells` /
   * `heightCells` are true at every level.
   */
  readonly zoom?: number | undefined;
  readonly onCameraChange: (c: CameraView) => void;
  readonly selection: Selection;
  readonly onSelect: (s: Selection) => void;
  readonly onHover: (t: HoverTarget) => void;
  readonly onOpenTown: (id: TownId) => void;
  readonly onCommand: (cmd: { readonly type: "MOVE_HERO"; readonly hero: HeroId; readonly path: readonly number[] }) => void;
  /** A click that reached an adjacent object rather than walking to it. The
   *  shell decides whether that opens a shop or issues `VISIT_OBJECT`; this
   *  canvas only reports where the click landed. */
  readonly onVisitObject?: (id: ObjectId) => void;
  /** A click on a town the hero has already walked up to, or cannot walk to at
   *  all. Whose town it is and what that makes the click - walking in, laying
   *  siege, or a refusal the player is owed - is the shell's to ask the kernel;
   *  this canvas only reports which town was clicked. */
  readonly onReachTown?: (id: TownId) => void;
  /**
   * Two of the player's own heroes meeting. Reported when the click lands on
   * an own hero who is NOT the selected one and is standing within a step of
   * him - The classic's "walk your hero onto your other hero", minus the walk, which
   * there is no room for when the two are already touching. The shell opens
   * the exchange; this canvas only says who met whom.
   */
  readonly onMeetHero?: (mover: HeroId, other: HeroId) => void;
  /**
   * An armed spell cursor, or null.
   *
   * While it is set this canvas answers a left click with `onAimAt` and nothing
   * else: no selection, no walk, no visit. That is the whole of targeting mode
   * here - which cell is legal, and what a refused one is told, are the shell's
   * and the kernel's.
   */
  readonly aiming?: Aiming | null;
  readonly onAimAt?: (x: number, y: number, z: number) => void;
  /** Escape, or the banner's own button. See `useEffect` on the key below. */
  readonly onAimCancel?: (() => void) | undefined;
  /**
   * A right press, and what it landed on.
   *
   * The right button already panned the map, and the classic already used it to open
   * an info card, so both live on it: the card goes up on the press and comes
   * down again the moment the pointer moves far enough to be a drag. A player
   * who wanted to pan sees a card for the few pixels before he moves; a player
   * who wanted to look holds still and reads it.
   */
  readonly onInfoAt?: ((target: HoverTarget, clientX: number, clientY: number) => void) | undefined;
  readonly onInfoDismiss?: (() => void) | undefined;
  /**
   * The phone's armed destination - the cell a first tap drew a path to -
   * whenever it changes, null when there is none. The shell's Info key reads
   * the card for it, since a thumb has no right button.
   */
  readonly onPendingTap?: ((cell: TouchCell | null) => void) | undefined;
  /** Pinch (or a host that wants a zoom step). +1 in, -1 out. */
  readonly onZoom?: ((dir: 1 | -1) => void) | undefined;
  /** Version the presenter should re-read on. */
  readonly version: number;
  readonly interactive: boolean;
}

/**
 * How close to the edge of the map view the pointer must come, in CSS pixels,
 * before the map scrolls under it, and how fast it then scrolls.
 *
 * The classic scrolls from a band a few pixels wide at the very edge of the screen.
 * The band is kept thin here for the same reason it is thin there: it sits on
 * top of tiles a player still has to be able to click, and every pixel of band
 * is a pixel where a click also starts the map moving. Scrolling only runs
 * while the canvas is interactive, so it cannot creep during a battle, during a
 * computer player's turn, or under an open screen.
 */
export const EDGE_BAND_PX = 8;
export const EDGE_SPEED_CELLS_PER_SEC = 7;

/**
 * Push a `CameraView` onto the presenter.
 *
 * `CameraView` is the top-left visible cell, which is what the minimap and the
 * arrow keys work in. `CameraState.x/y` is the world pixel the viewport is
 * CENTRED on. Converting in one place is the whole reason this function exists:
 * treating one as the other puts the map half a screen away and reads as "the
 * renderer draws nothing".
 */
export function applyCamera(p: AdventurePresenter, cam: CameraView): void {
  p.setLevel(cam.z);
  p.setCamera({
    x: (cam.x + cam.widthCells / 2) * TILE_SIZE,
    y: (cam.y + cam.heightCells / 2) * TILE_SIZE,
  });
}

/** How many cells a host of `w` x `h` CSS pixels shows at `zoom`. */
function cellsFor(w: number, h: number, zoom: number): { readonly widthCells: number; readonly heightCells: number } {
  const px = TILE_SIZE * (zoom > 0 ? zoom : 1);
  return {
    widthCells: Math.max(1, Math.floor(w / px)),
    heightCells: Math.max(1, Math.floor(h / px)),
  };
}

interface AtCell {
  readonly hero: ObservedHero | null;
  readonly town: ObservedTown | null;
  readonly object: ObservedObject | null;
}

function atCell(obs: PlayerObservation, x: number, y: number, z: number): AtCell {
  let hero: ObservedHero | null = null;
  let town: ObservedTown | null = null;
  let object: ObservedObject | null = null;
  for (const h of obs.heroes) if (h.x === x && h.y === y && h.z === z) hero = h;
  for (const t of obs.towns) if (t.x === x && t.y === y && t.z === z) town = t;
  for (const o of obs.objects) if (o.x === x && o.y === y && o.z === z) object = o;
  return { hero, town, object };
}

/**
 * The town a cell belongs to: its doorstep, or any cell of its building.
 *
 * A town is written down twice. The cell a hero walks into is an
 * `ObservedTown`; the six-by-four castle drawn over and above it is an
 * ordinary `town`-kind map object with a footprint, and the two meet only
 * through the object definition's entrance offset. Neither list answers "what
 * did I just click" on its own, which is why the roof of a castle - twenty
 * three of its twenty four cells - used to match nothing a click could use.
 *
 * Resolving both here, before anything is decided, is what lets the readings
 * below be owner-blind: clicking the enemy's castle is clicking a town in
 * exactly the way clicking your own is.
 */
function townAt(
  obs: PlayerObservation, at: AtCell, covering: ObservedObject | null,
  defOf: (def: string) => MapObjectDef | null,
): ObservedTown | null {
  if (at.town !== null) return at.town;
  if (covering === null || covering.kind !== "town") return null;
  for (const [dx, dy] of defOf(covering.def)?.entrances ?? []) {
    const ex = covering.x + dx, ey = covering.y + dy;
    for (const t of obs.towns) if (t.x === ex && t.y === ey && t.z === covering.z) return t;
  }
  return null;
}

/** What the pointer should say it will do. The renderer draws it; the kernel
 *  still decides whether it happens. */
function cursorFor(obs: PlayerObservation, at: AtCell, reachable: boolean): CursorKind {
  if (!reachable) return "blocked";
  const self = obs.self?.id ?? null;
  if (at.hero !== null && at.hero.owner !== self) return "attack";
  if (at.town !== null) return at.town.owner === self ? "enter" : "attack";
  if (at.object !== null) {
    if (at.object.kind === "monster") return "attack";
    if (at.object.kind === "resource" || at.object.kind === "artifact" || at.object.kind === "treasure") return "pickup";
    if (at.object.kind === "boat") return "boat";
    return "visit";
  }
  return "move";
}

export function WorldCanvas(props: WorldCanvasProps): JSX.Element {
  const { session, presenterRef, camera, onCameraChange, selection } = props;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drag = useRef<{
    x: number; y: number; moved: boolean; touch: boolean; longPress: boolean;
  } | null>(null);
  const pendingTap = useRef<TouchCell | null>(null);
  const onPendingTap = props.onPendingTap;
  const setPending = useCallback((cell: TouchCell | null): void => {
    const prev = pendingTap.current;
    pendingTap.current = cell;
    if (prev !== cell) onPendingTap?.(cell);
  }, [onPendingTap]);
  const skipClick = useRef(false);
  const longPressTimer = useRef(0);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinch = useRef<{ startDist: number; lastSteps: number } | null>(null);
  const phone = usePhoneLayout();
  // Bumped when the presenter finishes mounting. Every effect that pushes state
  // into it lists this, because the atlas takes seconds to decode and each of
  // those effects has already run and returned early by the time it lands.
  const [mountedAt, setMountedAt] = useState(0);
  // The presenter borrows the app's atlas and owns a WebGL context. It is mounted once
  // for the life of the canvas and re-pointed at whatever session is current;
  // keying it on `session` would tear down and re-upload every sheet on New
  // Game, which is both slow and a leak waiting to happen.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // The pointer and resize handlers must read the *current* camera, not the one
  // their closure captured. Without this a resize silently re-applies whatever
  // camera existed when the effect was created, which reads as "the map will
  // not scroll" and takes an hour to find.
  const cameraRef = useRef(camera);
  cameraRef.current = camera;
  const zoom = props.zoom ?? 1;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  // Read by the pointer handlers, which outlive the render that armed the
  // cursor for the same reason the camera does.
  const aimingRef = useRef<Aiming | null>(props.aiming ?? null);
  aimingRef.current = props.aiming ?? null;
  const phoneRef = useRef(phone.phone);
  phoneRef.current = phone.phone;
  const onZoomRef = useRef(props.onZoom);
  onZoomRef.current = props.onZoom;
  const actAtRef = useRef<((clientX: number, clientY: number, mode: "commit" | "arm") => void) | null>(null);

  const cancelAimRef = useRef<(() => void) | undefined>(props.onAimCancel);
  cancelAimRef.current = props.onAimCancel;

  // ---- Escape, taken in the capture phase and taken FIRST
  //
  // `App` binds Escape on the window in the capture phase and, on the adventure
  // screen with no overlay open, opens the system menu and calls
  // `stopImmediatePropagation` - deliberately, so that a system panel owns the
  // key. That makes every Escape binding below it, including the adventure
  // screen's own hotkey list, unreachable; measured here as "the spell cursor
  // survives Escape and the game menu opens on top of it".
  //
  // An armed cursor is the innermost thing on the screen and the one Escape
  // most obviously means, so it takes the key back the only way that works
  // without reaching into `App`: a capture listener registered from a CHILD,
  // whose effects React runs before the parent's, and therefore ahead of it in
  // the listener queue. It is registered once and reads the cursor through a
  // ref, so it never loses its place by re-subscribing.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== "Escape" || e.repeat) return;
      if (aimingRef.current === null) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      cancelAimRef.current?.();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => { window.removeEventListener("keydown", onKeyDown, true); };
  }, []);

  // The map's extent, for clamping. Read through a ref for the same reason the
  // camera is: the resize observer and the scroll loop outlive the render that
  // created them.
  const obsNow = session.getObservation();
  const mapRef = useRef<MapExtent>({
    width: obsNow.map.width, height: obsNow.map.height, levels: obsNow.map.levels,
  });
  mapRef.current = {
    width: obsNow.map.width, height: obsNow.map.height, levels: obsNow.map.levels,
  };

  // ---- mount / unmount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    // The app's own atlas, shared with the battle screen: see `game/gpuAtlas.ts`.
    const presenter = new AdventurePresenter({
      atlas: ensureGpuAtlas(), stepMs: 150, groundWash: groundWash(app.getSnapshot().content),
    });
    let live = true;
    const mounted = presenter.mount(canvas).then(() => {
      if (!live) { presenter.dispose(); return; }
      presenterRef.current = presenter;
      app.world = presenter as unknown as { readonly camera: unknown };
      const host = hostRef.current;
      if (host !== null) {
        presenter.resize(
          Math.max(1, Math.round(host.clientWidth)),
          Math.max(1, Math.round(host.clientHeight)),
        );
      }
      setMountedAt((n) => n + 1);
      const now = sessionRef.current;
      presenter.update({ obs: now.getObservation(), events: [], version: now.getVersion() });
    });
    mounted.catch((e: unknown) => {
      // Leaving the map while the mount is still in flight is not a failure.
      // The cleanup below disposes the presenter, `mount` then rejects against
      // a torn-down stage, and reporting that reaches the player as a
      // "world renderer failed to mount" dialog over the main menu he asked
      // for. Only a rejection while this canvas is still on screen is real.
      if (!live) return;
      // A WebGL context that never comes up is the one failure that looks
      // exactly like "the art is missing". Say so out loud.
      app.setError(`world renderer failed to mount: ${e instanceof Error ? e.message : String(e)}`);
    });

    const drop = app.addSettler("world", async () => {
      await mounted.catch(() => undefined);
      await presenterRef.current?.settled();
    });

    return () => {
      live = false;
      drop();
      if (app.world === (presenter as unknown)) app.world = null;
      if (presenterRef.current === presenter) presenterRef.current = null;
      presenter.dispose();
    };
  }, [presenterRef]);

  // ---- size
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const apply = (): void => {
      const w = Math.max(1, Math.round(host.clientWidth));
      const h = Math.max(1, Math.round(host.clientHeight));
      presenterRef.current?.resize(w, h);
      // Clamped, because a viewport that just grew can leave the old top-left
      // cell outside the map, and an unclamped camera reads as "the map will
      // not scroll back" - the renderer clamps its own copy, so the two then
      // disagree by however far the player has dragged past the edge. The
      // cell count is taken at the zoom the presenter actually uses; before
      // it has mounted, the asked-for one.
      onCameraChange(clampCamera({
        ...cameraRef.current,
        ...cellsFor(w, h, presenterRef.current?.zoom() ?? zoomRef.current),
      }, mapRef.current));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(host);
    return () => { ro.disconnect(); };
    // Re-run after mount: the first pass often sees a null presenter because
    // `mount()` is async, and without this the retina camera kept the
    // mount-time view (CSS / dpr) forever.
      }, [presenterRef, mountedAt]);

  // ---- observation
  useEffect(() => {
    const p = presenterRef.current;
    if (p === null) return;
    p.update({ obs: session.getObservation(), events: [], version: props.version });
  }, [session, presenterRef, mountedAt, props.version]);

  // ---- zoom: the viewport keeps its centre and changes its cell count
  useEffect(() => {
    const p = presenterRef.current;
    const host = hostRef.current;
    if (p === null || host === null) return;
    p.setZoom(zoom);
    const prev = cameraRef.current;
    const cells = cellsFor(host.clientWidth, host.clientHeight, p.zoom());
    // Around the same centre: a zoom that kept the top-left cell would slide
    // the view towards the south-east by a quarter of the map at each step.
    onCameraChange(clampCamera({
      ...prev,
      x: prev.x + prev.widthCells / 2 - cells.widthCells / 2,
      y: prev.y + prev.heightCells / 2 - cells.heightCells / 2,
      ...cells,
    }, mapRef.current));
    // `onCameraChange` is read at the level the effect ran at, like the size
    // effect above; the shell's is a stable callback.
  }, [zoom, presenterRef, mountedAt]);

  // ---- camera: the shell's view is the authority, `applyCamera` is the door
  useEffect(() => {
    const p = presenterRef.current;
    if (p === null) return;
    applyCamera(p, camera);
    // The fields are listed one by one, not the object: the camera is rebuilt on
    // every pan and only a real change should push the renderer.
  }, [presenterRef, mountedAt, camera.x, camera.y, camera.z, camera.widthCells, camera.heightCells]);

  // ---- the level changed under the pointer
  //
  // Hover is a cell, and a cell is (x, y, z). The pointer does not move when
  // the camera changes level, so nothing recomputed it: after stepping through
  // a subterranean gate the status plate went on describing the patch of grass
  // the pointer had last been over on the surface - naming surface terrain, and
  // quoting `UNREACHABLE_COST` for it, over a screen showing a cavern. The
  // hover is dropped instead, and the plate falls back to the selected hero's
  // own ground until the player moves the mouse and a real reading arrives.
  const hoverOff = props.onHover;
  useEffect(() => {
    presenterRef.current?.setHoverPath(null);
    hoverOff({ kind: "NONE" });
  }, [presenterRef, camera.z, hoverOff]);

  // ---- reachable overlay for the selected hero, or the armed spell's targets
  //
  // One overlay, two meanings, and they cannot both be on screen: a hero being
  // aimed with is not being walked. Showing the walk reach under a targeting
  // cursor would be showing the player the wrong set of cells - Blink Step
  // crosses ground he cannot walk to, which is the entire point of it.
  const aiming = props.aiming ?? null;
  useEffect(() => {
    const p = presenterRef.current;
    if (p === null) return;
    if (aiming !== null) {
      p.setReachable(aimingReach(mapRef.current, aiming), 0);
      return;
    }
    const hero = selection.heroId;
    if (hero === null) { p.setReachable(null, 0); return; }
    p.setReachable(session.reachableFor(hero), session.movementLeft(hero));
  }, [presenterRef, session, mountedAt, selection.heroId, props.version, aiming]);

  // ---- edge scroll
  //
  // The pointer resting in the band at the edge of the map area scrolls the map
  // under it, as it does in the classic. It is a deliberate pan like any other: the
  // camera stays where the player leaves it and nothing recentres it afterwards.
  //
  // The vector is a ref rather than state because it changes on every pointer
  // move and re-rendering the whole shell for it would be absurd; the loop that
  // reads it is the only thing that pushes a camera.
  const edge = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const edgeRaf = useRef(0);

  const edgeStop = useCallback((): void => {
    edge.current = { dx: 0, dy: 0 };
    if (edgeRaf.current !== 0) { cancelAnimationFrame(edgeRaf.current); edgeRaf.current = 0; }
  }, []);

  const edgeStart = useCallback((): void => {
    if (edgeRaf.current !== 0) return;
    let last = 0;
    const tick = (t: number): void => {
      const { dx, dy } = edge.current;
      if (dx === 0 && dy === 0) { edgeRaf.current = 0; return; }
      const dt = last === 0 ? 16 : Math.min(100, t - last);
      last = t;
      const step = (dt / 1000) * EDGE_SPEED_CELLS_PER_SEC;
      const cam = cameraRef.current;
      onCameraChange(clampCamera(
        { ...cam, x: cam.x + dx * step, y: cam.y + dy * step }, mapRef.current,
      ));
      edgeRaf.current = requestAnimationFrame(tick);
    };
    edgeRaf.current = requestAnimationFrame(tick);
  }, [onCameraChange]);

  // A battle, a computer player's turn or an open screen all clear `interactive`;
  // the map must not keep creeping under any of them just because the pointer
  // was left near an edge.
  useEffect(() => {
    if (!props.interactive) edgeStop();
    return edgeStop;
  }, [props.interactive, edgeStop]);

  const edgeAt = useCallback((clientX: number, clientY: number): void => {
    const host = hostRef.current;
    if (host === null || !props.interactive || phoneRef.current) return;
    const r = host.getBoundingClientRect();
    const dx = clientX - r.left < EDGE_BAND_PX ? -1 : r.right - clientX < EDGE_BAND_PX ? 1 : 0;
    const dy = clientY - r.top < EDGE_BAND_PX ? -1 : r.bottom - clientY < EDGE_BAND_PX ? 1 : 0;
    edge.current = { dx, dy };
    if (dx !== 0 || dy !== 0) edgeStart(); else edgeStop();
  }, [props.interactive, edgeStart, edgeStop]);

  // ---- pointer

  /**
   * What is under a screen point, as the same `HoverTarget` the status bar and
   * the info card both take.
   *
   * Extracted from `hoverAt` when the right button learned to open an info
   * card: the two have to agree about what "the castle" means at the edge of
   * its own footprint, and two copies of that rule would eventually disagree.
   */
  interface Picked {
    readonly at: AtCell;
    readonly x: number;
    readonly y: number;
    readonly z: number;
    readonly target: HoverTarget;
  }

  const pickAt = useCallback((clientX: number, clientY: number): Picked | null => {
    const p = presenterRef.current;
    const host = hostRef.current;
    if (p === null || host === null) return null;
    const box = host.getBoundingClientRect();
    // `clientX` and the rect are viewport CSS pixels; the presenter wants the
    // host's own layout pixels. Nothing scales the host's ancestors today (the
    // zoom-compensating root transform is gone), so `k` is 1 - it stays so
    // that a scaled ancestor added later cannot silently skew every pick.
    const k = box.width > 0 ? host.clientWidth / box.width : 1;
    const cell = p.pickCell(pixelPoint((clientX - box.left) * k, (clientY - box.top) * k));
    const x = cell.x as number, y = cell.y as number, z = cell.z;
    const obs = session.getObservation();
    if (x < 0 || y < 0 || x >= obs.map.width || y >= obs.map.height) return null;

    // The whole building answers for the town, not just its doorstep: a player
    // who points at a castle is pointing at that castle wherever on it the
    // pointer lands, and the status plate should say so.
    const defOf = (d: string): MapObjectDef | null => session.getContent().mapObjects.get(d) ?? null;
    const here = atCell(obs, x, y, z);
    const town = townAt(obs, here, here.object ?? objectCovering(obs.objects, defOf, x, y, z), defOf);
    const at: AtCell = { ...here, town };
    const target: HoverTarget = at.hero !== null ? { kind: "HERO", id: at.hero.id }
      : at.town !== null ? { kind: "TOWN", id: at.town.id }
        : at.object !== null ? { kind: "OBJECT", id: at.object.id }
          : { kind: "CELL", x, y, z };
    return { at, x, y, z, target };
  }, [presenterRef, session]);

  const hoverAt = useCallback((clientX: number, clientY: number): void => {
    const p = presenterRef.current;
    if (p === null) return;
    const picked = pickAt(clientX, clientY);
    if (picked === null) {
      props.onHover({ kind: "NONE" });
      p.setHoverPath(null);
      return;
    }
    const { at, x, y, z } = picked;
    const obs = session.getObservation();
    props.onHover(picked.target);

    // An armed cursor is not planning a walk: the route the hero would take to
    // the cell he is about to open a door onto is not a thing that will happen.
    // The cursor still changes on a legal target, so pointing at one says so.
    const aim = aimingRef.current;
    if (aim !== null) {
      p.setHoverPath({
        steps: [{ x, y, cost: 0 }],
        budget: 0,
        cursor: isAimTarget(mapRef.current, aim, x, y, z) ? "enter" : "blocked",
      });
      return;
    }

    const hero = selection.heroId;
    if (hero === null) { p.setHoverPath(null); return; }
    const path = session.pathFor(hero, x, y, z);
    if (path.length === 0) { p.setHoverPath(null); return; }

    const set = session.reachableFor(hero);
    const steps: PathStep[] = path.map((idx) => ({
      x: idx % obs.map.width,
      y: Math.floor(idx / obs.map.width) % obs.map.height,
      cost: set === null ? 0 : (set.cost[idx] ?? 0),
    }));
    const hoverPath: HoverPath = {
      steps,
      budget: session.movementLeft(hero),
      cursor: cursorFor(obs, at, true),
    };
    p.setHoverPath(hoverPath);
  }, [presenterRef, session, selection.heroId, props, pickAt]);

  const clearLongPress = useCallback((): void => {
    if (longPressTimer.current !== 0) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = 0;
    }
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const touchish = phoneRef.current || e.pointerType === "touch";
    if (touchish && (e.button === 0 || e.pointerType !== "mouse")) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      edgeStop();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      if (pointers.current.size >= 2) {
        clearLongPress();
        const pts = [...pointers.current.values()];
        const a = pts[0]!, b = pts[1]!;
        pinch.current = { startDist: pointerDistance(a, b), lastSteps: 0 };
        drag.current = { x: e.clientX, y: e.clientY, moved: true, touch: true, longPress: false };
        return;
      }
      drag.current = { x: e.clientX, y: e.clientY, moved: false, touch: true, longPress: false };
      if (props.interactive) {
        const sx = e.clientX, sy = e.clientY;
        longPressTimer.current = window.setTimeout(() => {
          longPressTimer.current = 0;
          const d = drag.current;
          if (d === null || d.moved) return;
          d.longPress = true;
          const picked = pickAt(sx, sy);
          if (picked !== null) props.onInfoAt?.(picked.target, sx, sy);
        }, LONG_PRESS_MS);
      }
      return;
    }
    if (e.button === 2 || e.button === 1) {
      drag.current = { x: e.clientX, y: e.clientY, moved: false, touch: false, longPress: false };
      edgeStop();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      if (e.button === 2 && props.interactive) {
        const picked = pickAt(e.clientX, e.clientY);
        if (picked !== null) props.onInfoAt?.(picked.target, e.clientX, e.clientY);
      }
    }
  }, [edgeStop, pickAt, props, clearLongPress]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    if (pointers.current.has(e.pointerId)) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pinch.current !== null && pointers.current.size >= 2) {
      const pts = [...pointers.current.values()];
      const dist = pointerDistance(pts[0]!, pts[1]!);
      const steps = pinchZoomSteps(pinch.current.startDist, dist);
      if (steps !== pinch.current.lastSteps) {
        const dir: 1 | -1 = steps > pinch.current.lastSteps ? 1 : -1;
        pinch.current.lastSteps = steps;
        onZoomRef.current?.(dir);
      }
      return;
    }
    const d = drag.current;
    if (d !== null) {
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (d.touch && !d.moved && pointerDistance({ x: 0, y: 0 }, { x: dx, y: dy }) < TOUCH_PAN_SLOP_PX) {
        return;
      }
      if (dx !== 0 || dy !== 0) {
        // The first pixel of movement is a pan, so the card comes down. A card
        // that survived the drag would follow the map around under the pointer.
        if (!d.moved) {
          clearLongPress();
          props.onInfoDismiss?.();
        }
        drag.current = { x: e.clientX, y: e.clientY, moved: true, touch: d.touch, longPress: d.longPress };
        const cam = cameraRef.current;
        // Clamped: an unclamped drag walks the view off the map and the player
        // then has to drag the same distance back before anything moves, which
        // reads as a dead map.
        onCameraChange(clampCamera(
          { ...cam, x: cam.x - dx / TILE_SIZE, y: cam.y - dy / TILE_SIZE }, mapRef.current,
        ));
      }
      return;
    }
    edgeAt(e.clientX, e.clientY);
    if (props.interactive) hoverAt(e.clientX, e.clientY);
  }, [onCameraChange, hoverAt, edgeAt, props, clearLongPress]);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    const d = drag.current;
    clearLongPress();
    if (d !== null && d.touch && !d.moved && !d.longPress && pointers.current.size === 0) {
      skipClick.current = true;
      actAtRef.current?.(e.clientX, e.clientY, "arm");
    } else if (d !== null && d.touch) {
      skipClick.current = true;
    }
    drag.current = pointers.current.size === 0 ? null : drag.current;
    // Same as a right-press: the card is up while the finger is down.
    props.onInfoDismiss?.();
  }, [props, clearLongPress]);

  const onPointerLeave = useCallback((): void => {
    pointers.current.clear();
    pinch.current = null;
    clearLongPress();
    drag.current = null;
    edgeStop();
    props.onInfoDismiss?.();
  }, [edgeStop, props, clearLongPress]);

  const actAt = useCallback((clientX: number, clientY: number, mode: "commit" | "arm"): void => {
    if (!props.interactive) return;
    const p = presenterRef.current;
    const host = hostRef.current;
    if (p === null || host === null) return;
    const box = host.getBoundingClientRect();
    const k = box.width > 0 ? host.clientWidth / box.width : 1;
    const cell = p.pickCell(pixelPoint((clientX - box.left) * k, (clientY - box.top) * k));
    const x = cell.x as number, y = cell.y as number, z = cell.z;
    const tapped: TouchCell = { x, y, z };

    // A spell is armed: every click on the map is the aim, including one on a
    // cell the kernel will refuse. Reporting the refusal is the shell's job and
    // it is why an illegal target is passed on rather than swallowed here - a
    // click that does nothing and says nothing is the bug this harness exists
    // to catch. Targeting is already two-step (arm the spell, then the hex),
    // so a phone tap commits it immediately.
    if (aimingRef.current !== null) {
      setPending(null);
      props.onAimAt?.(x, y, z);
      return;
    }

    const obs = session.getObservation();
    const self = obs.self?.id ?? null;
    const content = session.getContent();
    const defOf = (d: string): MapObjectDef | null => content.mapObjects.get(d) ?? null;
    const here = atCell(obs, x, y, z);
    const target = here.object ?? objectCovering(obs.objects, defOf, x, y, z);
    // What was clicked, decided once and for every owner. A town resolved here
    // is a town below, whoever holds it.
    const town = townAt(obs, here, target, defOf);

    // Selecting one of your own is always the first reading of a click, exactly
    // as in the classic: you cannot walk a hero onto the tile he is standing on.
    //
    // The exception is a selected hero standing on a doorstep. He is standing
    // on two things, and re-selecting a hero who is already selected changes
    // nothing and says nothing - which is precisely what happened to a hero who
    // had marched five days to the enemy's gate and then stood on it: every
    // further click on that cell was spent selecting him again. When the cell
    // is also a town, the town is the thing the click was about.
    if (here.hero !== null && here.hero.owner === self
      && !(town !== null && here.hero.id === props.selection.heroId)) {
      setPending(null);
      // A click on ANOTHER of your heroes standing beside the selected one is
      // the meeting, not a re-selection. Selecting him was the only reading
      // this canvas had, and it is the reading a player reports as "moving my
      // hero onto my ally just selects the other hero": in the classic two heroes
      // cannot share a cell at all, so walking into one opens the exchange,
      // and once they are already adjacent there is no walk left to make. The
      // roster still selects, so nothing is lost by spending this click here.
      const mover = props.selection.heroId === null ? null
        : obs.heroes.find((h) => h.id === props.selection.heroId) ?? null;
      if (mover !== null && here.hero.id !== mover.id && props.onMeetHero !== undefined
        && here.hero.z === mover.z
        && Math.abs(here.hero.x - mover.x) <= 1 && Math.abs(here.hero.y - mover.y) <= 1) {
        props.onMeetHero(mover.id, here.hero.id);
        return;
      }
      props.onSelect({ ...props.selection, heroId: here.hero.id });
      return;
    }
    if (town !== null && town.owner === self) {
      setPending(null);
      if (props.selection.townId === town.id) props.onOpenTown(town.id);
      else props.onSelect({ ...props.selection, townId: town.id });
      return;
    }

    const hero = props.selection.heroId;
    if (hero === null) return;

    // Open ground is a walk; a structure's own cells are impassable, so a click
    // on one is a reach from where the hero stands - or the walk that gets him
    // within reach. `readMapClick` is that reading, and it is pure.
    const set = session.reachableFor(hero);
    const width = obs.map.width, height = obs.map.height;
    const action = readMapClick({
      hero: obs.heroes.find((h) => h.id === hero) ?? null,
      object: target,
      def: target === null ? null : defOf(target.def),
      town,
      path: session.pathFor(hero, x, y, z),
      route: (cx, cy, cz) => ({
        path: session.pathFor(hero, cx, cy, cz),
        cost: set === null ? UNREACHABLE_COST
          : set.cost[cz * width * height + cy * width + cx] ?? UNREACHABLE_COST,
      }),
    });

    // Phone: first tap draws the path, second tap on the same cell walks it.
    if (mode === "arm") {
      const next = confirmOrArm(pendingTap.current, tapped);
      setPending(next.pending);
      hoverAt(clientX, clientY);
      if (!next.confirm) return;
    } else {
      setPending(null);
    }

    if (action.kind === "MOVE") props.onCommand({ type: "MOVE_HERO", hero, path: action.path });
    else if (action.kind === "VISIT") props.onVisitObject?.(action.object);
    else if (action.kind === "TOWN") props.onReachTown?.(action.town);
  }, [presenterRef, session, props, hoverAt]);
  actAtRef.current = actAt;

  const onClick = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (skipClick.current) { skipClick.current = false; return; }
    actAt(e.clientX, e.clientY, "commit");
  }, [actAt]);

  return (
    <div
      ref={hostRef}
      className={aiming === null ? "hm-world" : "hm-world hm-world--aiming"}
      style={aiming === null ? undefined : { cursor: "crosshair" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerLeave}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      onContextMenu={(e) => { e.preventDefault(); }}
    >
      <canvas ref={canvasRef} className="hm-world__canvas" />
    </div>
  );
}

/**
 * Section 31 - what each magic ground is washed in, for the renderer.
 *
 * Authored beside what the ground DOES (`MagicGroundDef.tint` / `tintBp`), and
 * turned into the renderer's own shape here, because the renderer may reach the
 * kernel's types but not its content. `#rrggbb` becomes the number Pixi wants
 * and basis points become the alpha; a bad colour is dropped rather than drawn
 * as black, which is what a failed parse would otherwise paint over the map.
 */
function groundWash(
  content: ContentRegistry | null,
): Readonly<Record<string, { readonly colour: number; readonly alpha: number }>> {
  const out: Record<string, { colour: number; alpha: number }> = {};
  for (const g of content?.magicGrounds.values() ?? []) {
    const colour = Number.parseInt(g.tint.replace("#", ""), 16);
    if (!Number.isFinite(colour)) continue;
    out[g.id] = { colour, alpha: Math.max(0, Math.min(10000, g.tintBp)) / 10000 };
  }
  return out;
}
